import React, { useState } from "react";
import { useSkillLevels, useUpdateSkillLevel } from "../../api/skillLevels";
import { StatusTag } from "../../components/StatusTag";
import { PaginationBar } from "../../components/PaginationBar";
import { useToast } from "../../components/ToastProvider";
import { SkillLevelModal } from "./SkillLevelModal";
import { EditIconButton } from "../../components/RowIconButton";
import { formatBRL } from "../../utils/money";
import { SkillLevel } from "../../types/dtos";

const PAGE_SIZE = 7;

export default function SkillLevelsPage() {
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<SkillLevel | null>(null);
  const [page, setPage] = useState(1);
  const { data: allData, isLoading, isError } = useSkillLevels(true);
  const update = useUpdateSkillLevel();
  const toast = useToast();

  const total = allData?.length ?? 0;
  const data = allData?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function handleToggleActive(level: SkillLevel) {
    try {
      await update.mutateAsync({ id: level.id, input: { active: !level.active } });
      toast(level.active ? "Nível desativado." : "Nível reativado.");
    } catch {
      toast("Não foi possível atualizar o status.");
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row g-2">
        <div className="col-md-9" />
        <div className="col-md-3">
          <button type="button" className="btn btn-primary w-100" onClick={() => setShowNew(true)}>
            Novo nível
          </button>
        </div>
      </div>

      {isLoading && <div className="text-secondary">Carregando…</div>}
      {isError && <div className="text-danger">Não foi possível carregar os níveis de habilidade.</div>}

      {data && (
        <div className="nt-table-wrap" style={{ overflowX: "auto" }}>
          <table className="table nt-table-fade table-striped align-middle">
            <thead>
              <tr>
                <th>Nível</th>
                <th className="text-end" style={{ width: 140 }}>
                  Valor/hora
                </th>
                <th style={{ width: 140 }}>Status</th>
                <th style={{ width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((l) => (
                <tr key={l.id}>
                  <td style={{ fontFamily: "var(--font-heading)" }}>{l.name}</td>
                  <td className="text-end nt-tabular">{formatBRL(l.hourlyRate)}</td>
                  <td>
                    <StatusTag status={l.active ? "ATIVO" : "INATIVO"} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      <EditIconButton onClick={() => setEditing(l)} />
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ fontSize: 12.5 }}
                        disabled={update.isPending}
                        onClick={() => handleToggleActive(l)}
                      >
                        {l.active ? "Desativar" : "Reativar"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {total === 0 && (
                <tr>
                  <td colSpan={4} className="text-secondary text-center py-4">
                    Nenhum nível de habilidade cadastrado ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {allData && <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />}

      <SkillLevelModal show={showNew} editing={null} onClose={() => setShowNew(false)} />
      <SkillLevelModal show={!!editing} editing={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
